import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import IconRocket from '@apollo/icons/default/IconRocket.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconSync from '@apollo/icons/default/IconSync.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Alert,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
  Tooltip,
} from '@apollo/orbit';
import { Switch } from '@apollo/space-kit/Switch';
import * as Sentry from '@sentry/react';
import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  contractsConfigRoute,
  editContractConfigRoute,
  launchesRouteConfig,
} from 'src/app/graph/routes';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { MultiSelect } from 'src/components/multiSelect/MultiSelect';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  ContractFilterConfigEditModalQuery,
  ContractFilterConfigEditModalQueryVariables,
  ContractPreviewErrors,
  PreviewContractQuery,
  PreviewContractQueryVariables,
  SubmitFilterCheckAsyncMutation,
  SubmitFilterCheckAsyncMutationVariables,
} from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { BuildResult } from 'src/lib/mock-utils/resolvers/types/codegenTypes';

import { TagList } from '../../../../../../components/tagList/TagList';
import { ContractConfigHeader } from '../ContractConfigHeader';
import {
  SchemaDiffMetaData,
  formatSchemaDiffMetaData,
} from '../contractConfigReviewModal/SchemaDiffMetaData';
import { ContractConfigStep, ContractConfigWorkflow } from '../steps';

import { ContractErrorsBanner } from './ContractErrorsBanner';
import { TypesAndFieldsDelta } from './typesAndFieldsDelta/TypesAndFieldsDelta';

const contractFilterConfigEditModalQuery = gql<
  ContractFilterConfigEditModalQuery,
  ContractFilterConfigEditModalQueryVariables
>`
  query ContractFilterConfigEditModalQuery(
    $graphId: ID!
    $sourceVariant: String!
    $contractName: String!
    $isCreateWorkflow: Boolean!
  ) {
    service(id: $graphId) {
      id
      title
      sourceVariant: variant(name: $sourceVariant) {
        id
        latestApprovedLaunch {
          id
          build {
            result {
              ... on BuildSuccess {
                coreSchema {
                  coreHash
                  apiDocument
                  coreDocument
                  fieldCount
                  typeCount
                  tags
                }
              }
            }
          }
        }
      }
      contractVariant: variant(name: $contractName)
        @skip(if: $isCreateWorkflow) {
        id
        contractFilterConfig {
          exclude
          include
          hideUnreachableTypes
        }
        latestApprovedLaunch {
          id
          build {
            input {
              ... on FilterBuildInput {
                filterConfig {
                  exclude
                  include
                  hideUnreachableTypes
                }
              }
            }
            result {
              ... on BuildSuccess {
                coreSchema {
                  coreHash
                  apiDocument
                  coreDocument
                  fieldCount
                  typeCount
                }
              }
            }
          }
        }
      }
    }
  }
`;

const previewContractQuery = gql<
  PreviewContractQuery,
  PreviewContractQueryVariables
>`
  query PreviewContractQuery(
    $sourceGraphRef: ID!
    # contract ref will only exist in edit workflows
    $contractGraphRef: ID!
    $filters: FilterConfigInput!
    $isCreateWorkflow: Boolean!
    $fetchLatestContract: Boolean!
  ) {
    variant(ref: $sourceGraphRef) {
      ... on GraphVariant {
        id
        contractPreview(filters: $filters) {
          result {
            ... on ContractPreviewSuccess {
              apiDocument
              coreDocument
              fieldCount
              typeCount
            }
            ... on ContractPreviewErrors {
              errors
              failedAt
            }
          }
          upstreamLaunch {
            id
            build {
              result {
                ... on BuildSuccess {
                  coreSchema {
                    apiDocument
                    coreDocument
                    coreHash
                    fieldCount
                    typeCount
                  }
                }
              }
            }
          }
        }
      }
    }
    latestContract: variant(ref: $contractGraphRef)
      @skip(if: $isCreateWorkflow) {
      ... on GraphVariant {
        id
        latestApprovedLaunch {
          id
          build @include(if: $fetchLatestContract) {
            input {
              ... on FilterBuildInput {
                schemaHash
                filterConfig {
                  exclude
                  include
                  hideUnreachableTypes
                }
              }
            }
            result {
              ... on BuildSuccess {
                coreSchema {
                  coreHash
                  apiDocument
                  coreDocument
                  fieldCount
                  typeCount
                }
              }
            }
          }
        }
      }
    }
  }
`;

const submitFilterCheckAsyncMutation = gql<
  SubmitFilterCheckAsyncMutation,
  SubmitFilterCheckAsyncMutationVariables
>`
  mutation SubmitFilterCheckAsyncMutation(
    $graphId: ID!
    $graphVariant: String!
    $checkConfig: FilterCheckAsyncInput!
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        submitFilterCheckAsync(input: $checkConfig) {
          ... on CheckRequestSuccess {
            workflowID
          }
        }
      }
    }
  }
`;

export interface ContractFilterConfigModalState {
  graphId?: string;
  graphTitle?: string;
  sourceVariant?: string;
  contractVariant?: string;
  includedTags?: Set<string>;
  excludedTags?: Set<string>;
  hideUnreachableTypes?: boolean;
  allTags?: Set<string>;
  sourceSchema?: SchemaDiffMetaData;
  proposedSchema?: SchemaDiffMetaData;
  latestSchema?: SchemaDiffMetaData;
  previewErrors?: ContractPreviewErrors;
  isNoOpPreview?: boolean;
  checkWorkflowId?: string;
}

interface Props {
  isOpen: boolean;
  state: ContractFilterConfigModalState | null;
  onGraphIdChange?: (graphId: string) => void;
  onSourceVariantChange?: (sourceVariant: string) => void;
  onContractVariantChange?: (contracVariant: string) => void;
  onGraphTitleChange?: (graphTitle: string) => void;
  onSchemaLoaded: (payload: {
    key: 'sourceSchema' | 'latestSchema';
    schemaValue: SchemaDiffMetaData;
  }) => void;
  onIncludedTagsChange: (includedTags: Set<string>) => void;
  onExcludedTagsChange: (excludedTags: Set<string>) => void;
  onHideUnreachableTypesChange: (hideUnreachableTypes: boolean) => void;
  saveAllTags: (allTags: Set<string>) => void;
  onContractPreviewTrigger: () => void;
  onContractPreviewResult: (
    result: {
      sourceSchema?: SchemaDiffMetaData;
      proposedSchema?: SchemaDiffMetaData;
      latestSchema?: SchemaDiffMetaData;
      isNoOpPreview?: boolean;
    } & { previewErrors?: ContractPreviewErrors },
  ) => void;
  onCheckWorkflowIdChange?: (checkWorkflowId: string) => void;
  onPrevious: () => void;
  onComplete: () => void;
  onClose: () => void;
  workflow: ContractConfigWorkflow;
}

export const ContractFilterConfigModal = ({
  isOpen,
  state,
  onGraphIdChange,
  onSourceVariantChange,
  onContractVariantChange,
  onGraphTitleChange,
  onSchemaLoaded,
  onIncludedTagsChange,
  onExcludedTagsChange,
  onHideUnreachableTypesChange,
  saveAllTags,
  onContractPreviewTrigger,
  onContractPreviewResult,
  onCheckWorkflowIdChange,
  onPrevious,
  onComplete,
  onClose,
  workflow,
}: Props) => {
  const {
    graphId: graphIdFromUrl,
    sourceVariant: sourceVariantFromUrl,
    contractVariant: contractVariantFromUrl,
  } = useRouteParams(
    workflow === ContractConfigWorkflow.edit
      ? editContractConfigRoute
      : contractsConfigRoute,
  );

  const { data: queryData, loading } = useQuery(
    contractFilterConfigEditModalQuery,
    {
      variables: {
        graphId: state?.graphId as string,
        sourceVariant: state?.sourceVariant as string,
        contractName: state?.contractVariant as string,
        isCreateWorkflow: workflow === ContractConfigWorkflow.create,
      },
      skip: !state?.graphId || !state?.contractVariant || !state?.sourceVariant,
    },
  );

  const [previewGeneratedOnce, setPreviewGeneratedOnce] = useState(
    Boolean(state?.proposedSchema || state?.previewErrors),
  );

  const [proposedFilterConfig, setProposedFilterConfig] = useState<{
    include: string[];
    exclude: string[];
    hideUnreachableTypes: boolean;
  }>({ include: [], exclude: [], hideUnreachableTypes: false });

  const [latestFilterConfig, setLatestFilterConfig] = useState<{
    include: string[];
    exclude: string[];
    hideUnreachableTypes: boolean;
  }>({ include: [], exclude: [], hideUnreachableTypes: false });

  // Set up the initial state of the modal, ONLY on the first time the modal is displayed
  useEffect(() => {
    const contractData = queryData?.service?.contractVariant;

    if (contractData?.contractFilterConfig && previewGeneratedOnce === false) {
      onIncludedTagsChange(new Set(contractData.contractFilterConfig.include));
      onExcludedTagsChange(new Set(contractData.contractFilterConfig.exclude));
      onHideUnreachableTypesChange(
        contractData.contractFilterConfig.hideUnreachableTypes,
      );

      setProposedFilterConfig(contractData.contractFilterConfig);

      setLatestFilterConfig(
        contractData?.latestApprovedLaunch?.build?.input.__typename ===
          'FilterBuildInput'
          ? contractData.latestApprovedLaunch.build.input.filterConfig
          : contractData.contractFilterConfig,
      );
    }

    if (previewGeneratedOnce === false) {
      const sourceSchema = formatSchemaDiffMetaData(
        queryData?.service?.sourceVariant?.latestApprovedLaunch?.build
          ?.result as BuildResult | undefined,
      );

      const latestSchema = formatSchemaDiffMetaData(
        contractData?.latestApprovedLaunch?.build?.result as
          | BuildResult
          | undefined,
      );

      if (sourceSchema) {
        onSchemaLoaded({
          key: 'sourceSchema',
          schemaValue: sourceSchema,
        });
      }

      if (latestSchema) {
        onSchemaLoaded({
          key: 'latestSchema',
          schemaValue: latestSchema,
        });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  const [sourceVariantTags, setSourceVariantTags] = useState<Set<string>>(
    new Set(),
  );

  const includedTags = useMemo(
    () => state?.includedTags ?? new Set<string>(),
    [state?.includedTags],
  );
  const excludedTags = useMemo(
    () => state?.excludedTags ?? new Set<string>(),
    [state?.excludedTags],
  );
  const hideUnreachableTypes = useMemo(
    () => state?.hideUnreachableTypes ?? false,
    [state?.hideUnreachableTypes],
  );

  const selectedTags = new Set([...includedTags, ...excludedTags]);
  const allTags = useMemo(
    () => new Set([...(state?.allTags ?? [])]),
    [state?.allTags],
  );

  const erroredTags = new Set(
    Array.from(includedTags).filter((tag) => excludedTags.has(tag)),
  );

  useEffect(() => {
    saveAllTags(
      new Set([
        ...allTags,
        ...sourceVariantTags,
        ...includedTags,
        ...excludedTags,
      ]),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceVariantTags, includedTags, excludedTags]);

  const sourceBuild =
    queryData?.service?.sourceVariant?.latestApprovedLaunch?.build?.result;

  useEffect(() => {
    setSourceVariantTags(
      sourceBuild?.__typename === 'BuildSuccess'
        ? new Set(sourceBuild.coreSchema.tags)
        : new Set(),
    );
  }, [sourceBuild]);

  useEffect(() => {
    if (!state?.graphId && graphIdFromUrl && onGraphIdChange) {
      onGraphIdChange(graphIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.graphId, graphIdFromUrl]);

  useEffect(() => {
    if (
      !state?.sourceVariant &&
      sourceVariantFromUrl &&
      onSourceVariantChange
    ) {
      onSourceVariantChange(sourceVariantFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.sourceVariant, sourceVariantFromUrl]);

  useEffect(() => {
    if (
      !state?.contractVariant &&
      contractVariantFromUrl &&
      onContractVariantChange
    ) {
      onContractVariantChange(contractVariantFromUrl as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.sourceVariant, sourceVariantFromUrl]);

  useEffect(() => {
    if (queryData?.service?.title && onGraphTitleChange) {
      onGraphTitleChange(queryData.service.title);
    }
    // Don't depend on the callback function changing here
    // since it results in an infinite render loop.
    // Ultimately we probably want to use a pattern where the wizard
    // can easily memoize these functions so they don't change on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData?.service?.title]);

  const onIncludedTagRemoved = useCallback(
    (tag: string) => {
      const newTags = new Set<string>(includedTags);
      newTags.delete(tag);
      onIncludedTagsChange(newTags);
    },
    [includedTags, onIncludedTagsChange],
  );

  const onExcludedTagRemoved = useCallback(
    (tag: string) => {
      const newTags = new Set<string>(excludedTags);
      newTags.delete(tag);
      onExcludedTagsChange(newTags);
    },
    [excludedTags, onExcludedTagsChange],
  );

  const [
    generateContractPreview,
    { data: previewData, error: previewErrors, loading: previewLoading },
  ] = useLazyQuery(previewContractQuery, { fetchPolicy: 'network-only' });

  const onGeneratePreview = () => {
    const include = Array.from(state?.includedTags ?? []);
    const exclude = Array.from(state?.excludedTags ?? []);

    onContractPreviewTrigger();
    setProposedFilterConfig({
      include,
      exclude,
      hideUnreachableTypes,
    });
    generateContractPreview({
      variables: {
        sourceGraphRef: `${state?.graphId}@${state?.sourceVariant}`,
        contractGraphRef: `${state?.graphId}@${state?.contractVariant}`,
        filters: {
          include,
          exclude,
          hideUnreachableTypes,
        },
        isCreateWorkflow: workflow === ContractConfigWorkflow.create,
        fetchLatestContract: workflow === ContractConfigWorkflow.edit,
      },
    });
  };

  const latestLaunchId =
    previewData?.latestContract?.__typename === 'GraphVariant' &&
    previewData.latestContract.latestApprovedLaunch?.id
      ? previewData.latestContract.latestApprovedLaunch.id
      : queryData?.service?.contractVariant?.latestApprovedLaunch?.id;

  useEffect(() => {
    if (previewData?.variant?.__typename === 'GraphVariant') {
      const maybeSourceHash =
        previewData.variant.contractPreview.upstreamLaunch.build?.result
          ?.__typename === 'BuildSuccess'
          ? previewData.variant.contractPreview.upstreamLaunch.build.result
              .coreSchema.coreHash
          : '';

      const maybeErrors =
        previewData.variant.contractPreview.result.__typename ===
        'ContractPreviewErrors'
          ? ({
              ...previewData.variant.contractPreview.result,
            } as ContractPreviewErrors)
          : undefined;

      const source = formatSchemaDiffMetaData(
        previewData.variant.contractPreview.upstreamLaunch.build?.result as
          | BuildResult
          | undefined,
      );

      const preview = formatSchemaDiffMetaData(
        previewData.variant.contractPreview.result,
      );

      let latest;
      let previewBuildInputMatchesLatestBuildInput = false;
      if (previewData?.latestContract?.__typename === 'GraphVariant') {
        latest = formatSchemaDiffMetaData(
          previewData.latestContract.latestApprovedLaunch?.build?.result as
            | BuildResult
            | undefined,
        );

        if (
          previewData.latestContract.latestApprovedLaunch?.build?.input
            .__typename === 'FilterBuildInput'
        ) {
          setLatestFilterConfig({
            include:
              previewData.latestContract.latestApprovedLaunch?.build?.input
                .filterConfig.include,
            exclude:
              previewData.latestContract.latestApprovedLaunch?.build?.input
                .filterConfig.exclude,
            hideUnreachableTypes:
              previewData.latestContract.latestApprovedLaunch?.build?.input
                .filterConfig.hideUnreachableTypes,
          });
          previewBuildInputMatchesLatestBuildInput =
            maybeSourceHash ===
              previewData.latestContract.latestApprovedLaunch.build.input
                .schemaHash &&
            isEqual(includedTags, new Set(latestFilterConfig.include)) &&
            isEqual(excludedTags, new Set(latestFilterConfig.exclude)) &&
            isEqual(
              hideUnreachableTypes,
              latestFilterConfig.hideUnreachableTypes,
            );
        }
      }

      if (!previewGeneratedOnce) setPreviewGeneratedOnce(true);
      onContractPreviewResult({
        sourceSchema: source,
        proposedSchema: preview,
        latestSchema: latest,
        previewErrors: maybeErrors,
        isNoOpPreview: previewBuildInputMatchesLatestBuildInput,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData]);

  // If we're returning to this step from a later step (Back from Review)
  // we need to set values for `proposedFilterConfig` from state instead of `previewData`
  useEffect(() => {
    if (previewGeneratedOnce && previewData === undefined) {
      setProposedFilterConfig({
        include: Array.from(includedTags),
        exclude: Array.from(excludedTags),
        hideUnreachableTypes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewGeneratedOnce, previewData]);

  // Compares the proposed filter configuration used
  // against the current selection.
  const filterConfigMatchesProposed =
    isEqual(includedTags, new Set(proposedFilterConfig.include)) &&
    isEqual(excludedTags, new Set(proposedFilterConfig.exclude)) &&
    isEqual(hideUnreachableTypes, proposedFilterConfig.hideUnreachableTypes);

  const compareAgainstLatest =
    workflow === ContractConfigWorkflow.edit && state?.latestSchema;

  const [submitFilterCheckAsync, { loading: checkWorkflowLoading }] =
    useMutation(submitFilterCheckAsyncMutation, {
      onError: (error) => Sentry.captureException(error),
      onCompleted: (res) => {
        if (
          res.service?.variant?.submitFilterCheckAsync.__typename ===
          'CheckRequestSuccess'
        ) {
          if (onCheckWorkflowIdChange) {
            onCheckWorkflowIdChange(
              res.service.variant.submitFilterCheckAsync.workflowID,
            );
          }
        }
      },
    });

  const submitFilterCheckAndGoToNextStep = async () => {
    if (
      workflow === ContractConfigWorkflow.create ||
      state?.proposedSchema === undefined // skip checks if we don't have a valid schema
    ) {
      onComplete(); // go to the next step
    }

    if (!state?.graphId || !state.contractVariant) {
      throw new Error(
        'Unexpectedly missing required information. Please try again or contact support.',
      );
    }

    const computeTagAdditions = (listA: string[], listB: string[]) =>
      listB.filter((tag) => !listA.includes(tag));
    const computeTagRemovals = (listA: string[], listB: string[]) =>
      listA.filter((tag) => !listB.includes(tag));

    await submitFilterCheckAsync({
      variables: {
        graphId: state.graphId,
        graphVariant: state.contractVariant,
        checkConfig: {
          config: {}, // no overrides / use defaults set by this variant
          gitContext: {}, // no git-context in this request
          filterChanges: {
            includeAdditions: computeTagAdditions(
              latestFilterConfig.include,
              proposedFilterConfig.include,
            ),
            includeRemovals: computeTagRemovals(
              latestFilterConfig.include,
              proposedFilterConfig.include,
            ),
            excludeAdditions: computeTagAdditions(
              latestFilterConfig.exclude,
              proposedFilterConfig.exclude,
            ),
            excludeRemovals: computeTagRemovals(
              latestFilterConfig.exclude,
              proposedFilterConfig.exclude,
            ),
            hideUnreachableTypesChange: hideUnreachableTypes,
          },
        },
      },
    });
    onComplete(); // go to the next step
  };

  // Advanced filter options drawer controls
  const [advancedFilterOptionsDrawerOpen, setAdvancedFilterOptionsDrawerOpen] =
    useState(false);

  useEffect(() => {
    // By default the advanced filter options drawer is collapsed,
    // we expand this section when at least one option (currently just hideUnreachableTypes)
    // is enabled or turned "on". This makes it clear to the user what the complete
    // filter configuration will be when generating the contract schema.
    //
    // Automatically expanding this section happens in a useEffect because
    // we only know this option is enabled after the network request has returned data.
    // The dependency on `hideUnreachableTypes` gets updated on component mount
    // in a useEffect above via `onHideUnreachableTypesChange`.
    if (hideUnreachableTypes) {
      setAdvancedFilterOptionsDrawerOpen(true);
    }
  }, [hideUnreachableTypes]);

  const showHideUnreachableTypesToggle = useLDFlag(
    'mercury_show_hide_unreachable_types_toggle',
  );

  return (
    <Modal isOpen={isOpen} size="3xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={submitFilterCheckAndGoToNextStep} noValidate>
        <ModalContent>
          <ModalHeader>
            <ContractConfigHeader
              currentStep={
                workflow === ContractConfigWorkflow.edit
                  ? ContractConfigStep.EditContract
                  : ContractConfigStep.FilterConfig
              }
              workflow={workflow}
            />
            <ModalTitle>
              {workflow === ContractConfigWorkflow.create && 'Contract Filters'}
              {workflow === ContractConfigWorkflow.edit && (
                <>
                  Edit {state?.graphId}@{state?.contractVariant}
                </>
              )}
            </ModalTitle>
            <ModalDescription>
              Contract schemas are updated when schema changes are published to{' '}
              <em>
                {state?.graphId}@{state?.sourceVariant}
              </em>
              . Schema elements are included or excluded from the contract
              schema according to the following filters.{' '}
              <ClickableText
                className="whitespace-nowrap text-link"
                as="a"
                rel="noopener noreferrer"
                target="_blank"
                href="https://apollographql.com/docs/studio/contracts/#tagging-rules"
              >
                Tagging rules
                <IconOutlink className="ml-1" />
              </ClickableText>
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <div className="flex flex-col items-center justify-center">
                <p className="pb-2 text-secondary">
                  Retrieving tag list for <em>{state?.sourceVariant}</em>{' '}
                  variant...
                </p>
                <Spinner size="sm" />
              </div>
            ) : previewErrors ? (
              <ErrorMessage />
            ) : (
              <>
                {!previewGeneratedOnce && sourceVariantTags?.size === 0 && (
                  <Alert status="info">
                    <b>No tags found in the source schema</b>. Schema elements
                    will only be removed if matching @tag directives are found
                    in your subgraph schema. Note you can still set up contract
                    filters before adding tags to your schema.
                  </Alert>
                )}
                {previewGeneratedOnce && selectedTags.size === 0 && (
                  <Alert status="info">
                    <b>No tags selected</b>. This tag configuration will
                    generate a schema identical to the source variant.
                  </Alert>
                )}
                {selectedTags.size > 0 &&
                  isEqual(includedTags, new Set(latestFilterConfig.include)) &&
                  isEqual(
                    excludedTags,
                    new Set(latestFilterConfig.exclude),
                  ) && (
                    <Alert status="info">
                      <div className="flex items-center">
                        This configuration is identical to the build input for
                        this contract's latest successful launch.&nbsp;
                        <Link
                          to={launchesRouteConfig.location({
                            graphId: state?.graphId as string,
                            graphVariant: state?.contractVariant as string,
                            launchId: latestLaunchId,
                          })}
                          className="flex items-center whitespace-nowrap"
                        >
                          View Launch
                          <IconRocket className="ml-1" />
                        </Link>
                      </div>
                    </Alert>
                  )}

                {/* Tag selection section */}
                {/* The need to preventDefault here is suspicious.  It is probably
                 * fixed in the commit where this comment was added, but
                 * since there are so many things in this section it's hard to know.
                 */}
                <section onClick={(evt) => evt.preventDefault()}>
                  <FormControl>
                    <FormLabel>
                      <Tooltip
                        placement="right"
                        label={
                          <span className="font-normal">
                            Schema elements with the following tags will be{' '}
                            <span className="font-semibold">excluded</span> from
                            your contract schema.
                          </span>
                        }
                      >
                        <div className="flex w-32 items-center text-base">
                          <Heading as="h3" variant="title">
                            Excluded tags
                          </Heading>
                          <IconInfo className="ml-2 text-secondary" />
                        </div>
                      </Tooltip>
                    </FormLabel>
                    <MultiSelect
                      disabled={previewLoading || checkWorkflowLoading}
                      className="w-120 max-w-full"
                      valueTitle="Tags"
                      allValues={Array.from(allTags).filter(
                        (tag) => selectedTags.has(tag) === false,
                      )}
                      canSearch={false}
                      canAddNewValues
                      onValueAdded={(tag) =>
                        onExcludedTagsChange(new Set([...excludedTags, tag]))
                      }
                      selectedValues={excludedTags}
                      setSelectedValues={onExcludedTagsChange}
                      triggerNode={
                        <TagList
                          tags={excludedTags}
                          erroredTags={erroredTags}
                          onTagRemoved={onExcludedTagRemoved}
                          disabled={previewLoading}
                        />
                      }
                    />
                  </FormControl>
                  <FormControl
                    className="mt-4"
                    isInvalid={erroredTags.size > 0}
                  >
                    <FormLabel>
                      <Tooltip
                        placement="right"
                        label={
                          <span className="font-normal">
                            Schema elements with the following tags will be{' '}
                            <span className="font-semibold">included</span> in
                            your contract schema.
                          </span>
                        }
                      >
                        <div className="flex flex-row items-center">
                          <Heading as="h3" variant="title">
                            Included tags
                          </Heading>
                          <IconInfo className="ml-2 text-secondary" />
                        </div>
                      </Tooltip>
                    </FormLabel>
                    <MultiSelect
                      disabled={previewLoading || checkWorkflowLoading}
                      className="w-120 max-w-full"
                      valueTitle="Tags"
                      allValues={Array.from(allTags).filter(
                        (tag) => selectedTags.has(tag) === false,
                      )}
                      canSearch={false}
                      canAddNewValues
                      onValueAdded={(tag) =>
                        onIncludedTagsChange(new Set([...includedTags, tag]))
                      }
                      selectedValues={includedTags}
                      setSelectedValues={onIncludedTagsChange}
                      triggerNode={
                        <TagList
                          tags={includedTags}
                          erroredTags={erroredTags}
                          onTagRemoved={onIncludedTagRemoved}
                          disabled={previewLoading || checkWorkflowLoading}
                        />
                      }
                    />
                    <FormErrorMessage>
                      Tags cannot be defined for both include and exclude
                      filters.
                    </FormErrorMessage>
                  </FormControl>
                </section>

                {/* Advanced filters section */}
                {showHideUnreachableTypesToggle && (
                  <section>
                    <Button
                      variant="buttonLink"
                      type="button"
                      onClick={() =>
                        setAdvancedFilterOptionsDrawerOpen((prev) => !prev)
                      }
                      rightIcon={
                        advancedFilterOptionsDrawerOpen ? (
                          <IconChevronUp />
                        ) : (
                          <IconChevronDown />
                        )
                      }
                    >
                      Advanced filter options
                    </Button>
                    {advancedFilterOptionsDrawerOpen && (
                      <div className="mb-6 flex justify-between">
                        <p>
                          Hide unreachable types in your contract schema.{' '}
                          <Link
                            className="whitespace-nowrap"
                            isExternal
                            to="https://apollographql.com/docs/studio/contracts/#contract-filters"
                          >
                            Learn more
                          </Link>
                        </p>
                        <Switch
                          aria-label="Enable"
                          isSelected={state?.hideUnreachableTypes}
                          onChange={onHideUnreachableTypesChange}
                          className="text-sm text-link"
                          isDisabled={loading || previewLoading}
                        />
                      </div>
                    )}
                  </section>
                )}

                {/* Types and fields sections */}
                <section className="flex flex-col">
                  <div className="flex justify-between">
                    <TypesAndFieldsDelta
                      originalTitle={
                        <Tooltip
                          placement="top-start"
                          label={
                            <span>
                              The API schema produced by the most recent
                              successful launch of{' '}
                              <b>
                                {state?.graphId}@
                                {compareAgainstLatest
                                  ? state?.contractVariant
                                  : state?.sourceVariant}
                              </b>
                            </span>
                          }
                        >
                          <span className="flex items-center">
                            {compareAgainstLatest
                              ? 'Latest Contract Schema'
                              : 'Source Schema'}
                            <IconInfo className="ml-2 text-secondary" />
                          </span>
                        </Tooltip>
                      }
                      proposedTitle="Proposed Contract Schema"
                      originalTypeCount={
                        compareAgainstLatest
                          ? state?.latestSchema?.typeCount
                          : state?.sourceSchema?.typeCount
                      }
                      originalFieldCount={
                        compareAgainstLatest
                          ? state?.latestSchema?.fieldCount
                          : state?.sourceSchema?.fieldCount
                      }
                      proposedTypeCount={state?.proposedSchema?.typeCount}
                      proposedFieldCount={state?.proposedSchema?.fieldCount}
                    />
                    <div className="flex flex-1 flex-col items-end">
                      <Button
                        variant="primary"
                        type="button"
                        size="sm"
                        className="mt-2"
                        onClick={onGeneratePreview}
                        isLoading={previewLoading}
                        loadingText="Generate Preview"
                        isDisabled={
                          (filterConfigMatchesProposed &&
                            previewGeneratedOnce) ||
                          checkWorkflowLoading
                        }
                      >
                        Generate Preview
                      </Button>

                      <span className="mt-6 flex items-center text-sm text-secondary">
                        {previewLoading && (
                          <>
                            <IconSync className="mr-1 text-icon-primary" />
                            Generating preview. This may take a moment...
                          </>
                        )}
                        {!previewLoading &&
                          (!previewGeneratedOnce ||
                            (!filterConfigMatchesProposed &&
                              !state?.previewErrors)) && (
                            <>
                              <IconInfo className="mr-1 text-icon-primary" />
                              Configure tag filters and generate preview.
                            </>
                          )}
                        {!previewLoading &&
                          filterConfigMatchesProposed &&
                          state?.proposedSchema && (
                            <>
                              <IconSuccess className="mr-1 text-icon-success" />
                              Preview Generated. Continue to review changes.
                            </>
                          )}
                        {!previewLoading && state?.previewErrors && (
                          <>
                            <IconWarning className="mr-1 text-icon-primary" />
                            Update your tags to generate a valid schema.
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                  {state?.previewErrors && (
                    <div className="mt-3">
                      <ContractErrorsBanner
                        errors={state.previewErrors.errors}
                        failedAt={state.previewErrors.failedAt}
                      />
                    </div>
                  )}
                </section>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {workflow === ContractConfigWorkflow.create && (
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
            )}
            <ButtonGroup>
              {workflow === ContractConfigWorkflow.create && (
                <Button variant="secondary" type="button" onClick={onPrevious}>
                  Back
                </Button>
              )}
              {workflow === ContractConfigWorkflow.edit && (
                <Button variant="secondary" type="button" onClick={onClose}>
                  Cancel
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                isLoading={checkWorkflowLoading}
                loadingText="Review"
                isDisabled={
                  loading ||
                  previewLoading ||
                  erroredTags.size > 0 ||
                  !previewGeneratedOnce ||
                  !filterConfigMatchesProposed
                }
              >
                Review
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
